import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './suppressed-mention.css';

export type MentionSuppressionOutcome =
  | 'no_action_requested'
  | 'blocked_by_policy'
  | 'confirmation_failed';

const OUTCOME_LABELS: Record<MentionSuppressionOutcome, string> = {
  no_action_requested: 'No action requested',
  blocked_by_policy: 'Mention blocked by policy',
  confirmation_failed: 'Mention check failed',
};

const TOOLTIP_GAP = 7;
const VIEWPORT_GUTTER = 8;

interface TooltipPosition {
  left: number;
  top: number;
  placement: 'above' | 'below';
}

const SuppressedMention: React.FC<{
  name: string;
  outcome: MentionSuppressionOutcome;
}> = ({ name, outcome }) => {
  const tooltipId = useId();
  const mentionRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const isVisible = isHovered || isFocused;

  const updatePosition = useCallback(() => {
    const mention = mentionRef.current;
    const tooltip = tooltipRef.current;
    if (!mention || !tooltip) return;

    const mentionRect = mention.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const halfWidth = tooltipRect.width / 2;
    const minimumLeft = VIEWPORT_GUTTER + halfWidth;
    const maximumLeft = Math.max(
      minimumLeft,
      window.innerWidth - VIEWPORT_GUTTER - halfWidth,
    );
    const centeredLeft = mentionRect.left + mentionRect.width / 2;
    const left = Math.min(maximumLeft, Math.max(minimumLeft, centeredLeft));
    const fitsAbove =
      mentionRect.top - TOOLTIP_GAP - tooltipRect.height >= VIEWPORT_GUTTER;
    const placement = fitsAbove ? 'above' : 'below';
    const top = fitsAbove
      ? mentionRect.top - TOOLTIP_GAP
      : Math.min(
          window.innerHeight - VIEWPORT_GUTTER - tooltipRect.height,
          mentionRect.bottom + TOOLTIP_GAP,
        );

    setPosition({ left, top, placement });
  }, []);

  useLayoutEffect(() => {
    if (isVisible) updatePosition();
  }, [isVisible, updatePosition]);

  useEffect(() => {
    if (!isVisible) return;

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isVisible, updatePosition]);

  const tooltip = (
    <span
      ref={tooltipRef}
      id={tooltipId}
      role="tooltip"
      className={`suppressed-mention-tooltip suppressed-mention-tooltip-${
        position?.placement ?? 'above'
      }${isVisible && position ? ' suppressed-mention-tooltip-visible' : ''}`}
      style={
        position === null
          ? undefined
          : { left: position.left, top: position.top }
      }>
      {OUTCOME_LABELS[outcome]}
    </span>
  );

  return (
    <>
      <span
        ref={mentionRef}
        className={`suppressed-mention suppressed-mention-${outcome}`}
        tabIndex={0}
        aria-describedby={tooltipId}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}>
        {name}
      </span>
      {typeof document === 'undefined'
        ? null
        : createPortal(tooltip, document.body)}
    </>
  );
};

export default SuppressedMention;
